<template>
  <div>
    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      :center-options="centerOptions"
      :sector-options="sectorOptions"
      :type-options="cameraTypeOptions"
      :unit-options="unitOptions"
      :master-account-options="masterAccountOptions"
      :reseller-options="resellerOptions"
      :user-options="userOptions"
    />

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>
    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />
    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :center-filter.sync="centerFilter"
          :center-options="centerOptions"
          :sector-filter.sync="sectorFilter"
          :sector-options="sectorOptions"
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :master-account-filter.sync="masterAccountFilter"
          :master-account-options="masterAccountOptions"
          :online-filter.sync="onlineFilter"
          :reseller-options="resellerOptions"
          :reseller-filter.sync="resellerFilter"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card-group deck class="mx-2">
      <b-card class="m-0" no-body>
        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
                data-cy="per-page-selector"
              />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <!-- Search -->
            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  data-cy="search-input"
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('table.search') + '...'"
                />
                <b-button
                  v-if="$can('create', 'PbxDevice')"
                  variant="secondary"
                  @click="openAdd()"
                >
                  <span class="text-nowrap text-capitalize">
                    {{ $t("actions.add") + " " + $tc("pbx_device.title", 1) }}
                  </span>
                </b-button>
                <b-button
                  @click="exportExcel()"
                  variant="success"
                  class="text-capitalize ml-1"
                  type="button"
                  :disabled="buttonExport"
                >
                  <feather-icon icon="DownloadCloudIcon" size="15" />
                  <span class="text-nowrap text-capitalize"> </span>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>
        <b-table
          ref="refListTable"
          class="position-relative"
          :items="fetchList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          :empty-text="$t('no_record_found')"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #head()="data">
            <span> {{ $tc(data.label, 1) }}</span>
          </template>

          <template #head(unit.description)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #cell(password)="data">
            <div
              v-clipboard:copy="`${data.value}`"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
              style="cursor: pointer"
            >
              <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
            </div>
          </template>

          <template #head(actions)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <!-- Column: status -->
          <template #cell(name)="data">
            <div style="display: inline-flex">
              <div
                class="mr-1"
                :style="`background: ${data.item.cameras_proxy.online ? 'green' : 'red'};
                      width: 1.5rem;
                      height: 1.5rem;
                      border-radius: 1rem;
                      text-align: center;`"
              ></div>
              {{ data.item.name }}
            </div>
          </template>

          <template #cell(rec_limit)="data">
            <span>
              {{ transformRecLimit(data.item.rec_limit) }}
            </span>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
              <template #button-content>
                <feather-icon
                  data-cy="options"
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item>
                <feather-icon icon="RefreshCwIcon" />
                <span class="align-middle ml-50 text-capitalize"
                  >{{ $t("actions.update") }}
                </span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('update', 'PbxDevice')"
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t("actions.edit") }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('delete', 'PbxDevice')"
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize"
                  >{{ $t("actions.delete") }}
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted text-capitalize">{{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <Pagination
                :totalList="totalList"
                :currentPage="currentPage"
                @updatePagination="update"
                :perPage="perPage"
                data-cy="pagination"
              >
              </Pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-card-group>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Filters from "./Filters.vue";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";

export default {
  components: {
    Sidenav,
    BreadcrumbMenu,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
  },
  props: {
    filters: {
      type: Object | Array,
    },
  },
  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const action = ref("list");
    const { t } = useI18nUtils();
    const trans = {
      "messages.export.download": t("messages.export.download"),
      "messages.export.Generating_file": t("messages.export.Generating_file"),
      "messages.error.server_error": t("messages.error.server_error"),
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
    };
    const exportExcel = () => {
      action.value == "export";
      buttonExport.value = true;
      loadingToast.value.show(
        trans["messages.export.download"],
        trans["messages.export.Generating_file"],
        "secondary"
      );
      store
        .dispatch("pbx_device/export", {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          sector: sectorFilter.value,
          center: centerFilter.value,
          action: "export",
          id_master_account: masterAccountFilter.value,
        })
        .then(() => {})
        .catch((error) => {
          console.error(error)
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.error.server_error"],
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          loadingToast.value.close();
          buttonExport.value = false;
        });
    };
    const buttonExport = ref(false);
    // end export

    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
    };

    const toast = useToast();

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,

      refListTable,
      refetchData,
      sectorOptions,
      sectorFilter,
      centerOptions,
      centerFilter,
      unitOptions,
      tenantOptions,
      tenantFilter,
      cameraTypeOptions,
      masterAccountOptions,
      masterAccountFilter,
      onlineFilter,
      buttonUpdate,
      loadingToast,

      clearSearchFilters,
      filtersCount,
      buildFilters,

      resellerOptions,
      resellerFilter,

      userOptions,
      userFilter,

      setCenterName,
      setSectorName,
      setMasterAccountName,
      setUnitsName,
      setUserName
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("pbx_device/delete", id)
            .then((response) => {
              if (response.status) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const onCopy = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "Copiado correctamente",
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    const onError = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "No se pudo copiar",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    const loadingToastEvent = function (data) {
      if (data.data) {
        loadingToast.value.show("Actualizando", `Actualizando cámaras ...`, "secondary");
      } else {
        this.$bvModal.hide("filter_modal");
        loadingToast.value.close();
        resellerFilter.value = null;
        refetchData();
        toast({
          component: ToastificationContent,
          props: {
            title: "Actualizadó correctamente",
            icon: "CheckIcon",
            variant: "success",
          },
        });
      }
    };

    return {
      loadingToast,
      action,
      exportExcel,
      buttonExport,
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      sectorOptions,
      sectorFilter,
      centerOptions,
      centerFilter,
      unitOptions,
      update,
      onCopy,
      onError,
      tenantOptions,
      tenantFilter,
      cameraTypeOptions,
      masterAccountOptions,
      masterAccountFilter,
      onlineFilter,
      token: localStorage.additionalTokenImage,
      buttonUpdate,
      loadingToastEvent,

      filtersCount,
      buildFilters,
      clearSearchFilters,

      resellerOptions,
      resellerFilter,

      userOptions,
      userFilter,

      setCenterName,
      setSectorName,
      setMasterAccountName,
      setUnitsName,
      setUserName,
    };
  },
};
</script>
